/**
 * Links, the host's half: which screens a member may link from, one fetch per
 * source screen for the page, and the panel materialised from them
 * (`docs/plans/screen-links.md`, Part 2; the pure half is
 * `@qufolio/screener-grid`'s `lib/screen-links.ts`).
 *
 *     the section          stores links           {field, link, width?, hide?, pinned?}
 *     useScreenLinks       materialises them      the source's column, the member's view
 *     updateScreener       strips them again      every patch, on the way in
 *
 * **Module scope with subscribers**, `screens.ts`'s shape and its reason: the
 * grid's `ScreenerView` and the docked builder in `App` both resolve the same
 * links, and two caches would be two fetches of Persons and two answers that
 * could disagree. Ten links to one screen are one fetch.
 *
 * **Keyed by the screen and its published version.** Publishing bumps the
 * version the catalog lists, the catalog is re-read, and the next render asks
 * for `jp-persons@8` rather than `@7` — so a group's format change reaches a
 * member's link without a reload of anything but the source.
 */

import { useEffect, useMemo, useSyncExternalStore } from "react";
import type { ScreenerConfig } from "@qufolio/model";
import {
  linkedScreens,
  materialiseLinks,
  type CopySourceDefinition,
  type ScreenLinks,
} from "@qufolio/screener-grid";

import { groupDisplayName } from "./group-layouts";
import { isAuthored, type Screen } from "./screens";

type Load = (key: string) => Promise<CopySourceDefinition | null>;

/** `undefined` while loading, `null` when the screen cannot be read. */
const sources = new Map<string, CopySourceDefinition | null | undefined>();
const listeners = new Set<() => void>();
let version = 0;

function notify(): void {
  version += 1;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The cache key: the screen, and the version the catalog says is published. */
export function sourceKey(key: string, screens: readonly Screen[]): string {
  const published = screens.find((s) => s.key === key)?.version;
  return published === undefined ? key : `${key}@${published}`;
}

/** Ask for a source once; a second ask for the same key is the cache. */
export function requestSource(cacheKey: string, key: string, load: Load): void {
  if (sources.has(cacheKey)) return;
  sources.set(cacheKey, undefined);
  void load(key).then(
    (definition) => {
      sources.set(cacheKey, definition);
      notify();
    },
    () => {
      sources.set(cacheKey, null);
      notify();
    },
  );
}

/** What resolution holds for a key now. */
export function sourceFor(cacheKey: string): CopySourceDefinition | null | undefined {
  return sources.get(cacheKey);
}

/** Drop every source (tests; a sign-out). */
export function forgetLinkSources(): void {
  sources.clear();
  notify();
}

/**
 * The screens a member may link from: **those of groups they hold**. The
 * catalog lists only what the caller can read, so a group screen in it is one
 * they hold; their own screens and the product's are not sources — a link
 * names a group's screen, the thing that changes under it.
 */
export function linkableScreens(screens: readonly Screen[]): ScreenLinks["screens"] {
  return screens
    .filter((s) => s.ownerKind === "org" && !!s.ownerKey)
    .map((s) => ({ key: s.key, name: s.name, ownerName: groupDisplayName(s.ownerKey!, s.ownerName) }));
}

/**
 * The links binding, or `undefined` while the open screen is **a group's
 * screen being authored** — decision 5 of `docs/plans/screen-links.md`: a link
 * in a group's screen would point every member at a group they may not hold,
 * so the picker is not offered there (the server refuses one anyway) and the
 * author uses Copy from screen…. A member's own screens and their layer keep it.
 */
export function linksOffered(
  links: ScreenLinks,
  screens: readonly Screen[],
  screenRef: string | undefined,
  publishTo: string | null | undefined,
): ScreenLinks | undefined {
  const open = screenRef ? screens.find((s) => s.key === screenRef) : undefined;
  return open && isAuthored(open, publishTo) ? undefined : links;
}

/**
 * The panel with its links resolved, and the binding the builder and the
 * picker read — `ScreenLinks`: the linkable screens, the loader, who each
 * source is, and which links found nothing.
 *
 * `load` is the draft-aware `screenLoader`, so an author linking from the group
 * they are authoring sees their draft; everybody else gets the published
 * screen, sealed as the server sends it to them.
 */
export function useScreenLinks<C extends ScreenerConfig | undefined>(input: {
  /** Undefined while there is no screener — a hook cannot be skipped. */
  config: C;
  screens: readonly Screen[];
  load: Load;
  /** The server's label for a field, for a placeholder's header. */
  labelOf?: (field: string) => string | undefined;
}): { config: C; links: ScreenLinks } {
  const { config, screens, load, labelOf } = input;
  const seen = useSyncExternalStore(subscribe, () => version);

  const wanted = useMemo(() => (config ? linkedScreens(config) : []), [config]);
  const wantedKey = wanted.map((k) => sourceKey(k, screens)).join(",");
  useEffect(() => {
    // **Not before the catalog has arrived.** The key carries the version the
    // catalog lists, so asking first would fetch `jp-persons`, then
    // `jp-persons@3` a moment later — every link, twice, on every page load.
    if (screens.length === 0) return;
    for (const key of wanted) requestSource(sourceKey(key, screens), key, load);
    // `wantedKey` carries both the keys and their versions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantedKey, load]);

  return useMemo(() => {
    const describe = (key: string) => {
      const screen = screens.find((s) => s.key === key);
      return screen
        ? {
            name: screen.name,
            ownerName: screen.ownerKey ? groupDisplayName(screen.ownerKey, screen.ownerName) : "",
          }
        : undefined;
    };
    const resolved = config && wanted.length
      ? materialiseLinks(config, (key) => sourceFor(sourceKey(key, screens)), {
          screenName: (key) => describe(key)?.name,
          labelOf,
        })
      : null;
    const materialised = (config && resolved
      ? {
          ...config,
          ...(resolved.columns ? { columns: resolved.columns } : {}),
          ...(resolved.savedFilters ? { savedFilters: resolved.savedFilters } : {}),
        }
      : config) as C;
    return {
      config: materialised,
      links: {
        screens: linkableScreens(screens),
        load,
        describe,
        missing: resolved?.missing ?? new Set(),
      },
    };
    // `seen` is the cache's version: a source arriving re-materialises.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, screens, load, labelOf, seen, wantedKey]);
}
